import * as Yup from "yup";
import {
  faLongArrowAltDown,
  faLongArrowAltUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { capitalize } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Button, Col, Form, Input, Label, Row, Table } from "reactstrap";
import SurflineAPI from "../../api/SurflineAPI";
import { getSwellsforStartTime, getTidesForSesh } from "../../util/surfline";
import { useStoreActions } from "easy-peasy";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import globalStyles from "../../util/styles";

const styles = {
  ...globalStyles,
  section: {
    marginTop: "1.5em",
  },
};

const swellTideSchema = Yup.object().shape({
  swellsAttributes: Yup.array()
    .of(
      Yup.object().shape({
        direction: Yup.number()
          .min(0, "Direction must be greater than 0°")
          .max(360, "Direction must be less than 360°")
          .integer("Direction must be whole number")
          .required("Required"),
        height: Yup.number()
          .min(0, "Height must be greater than 0")
          .required("Required"),
        period: Yup.number()
          .min(0, "Period must be greater than 0")
          .integer("Period must be whole number")
          .required("Required"),
      })
    )
    .required("Required"),
  tidesAttributes: Yup.array()
    .of(
      Yup.object().shape({
        height: Yup.number().required("Required"),
        rising: Yup.bool().required("Required"),
      })
    )
    .required("Required"),
});
const mapDataSourceToSelect = (dataSource, index) => ({
  id: dataSource.uniqueId,
  label: `${dataSource.name} - ${capitalize(dataSource.vendor)}`,
  uniqueId: dataSource.uniqueId,
  value: index,
});

const defaultValues = {
  swells: [],
  tides: [],
};

const SwellFetcher = ({ formData, next, prev }) => {
  const values = Object.keys(formData)?.length ? formData : defaultValues;
  const methods = useForm({
    defaultValues: values,
    resolver: yupResolver(swellTideSchema),
    mode: "onTouched",
  });
  const { spot } = formData;

  const initialSwellSource = spot?.dataSources
    ?.filter((s) => s.category === "swell")
    ?.map(mapDataSourceToSelect)[0];
  const initialTideSource = spot?.dataSources
    ?.filter((s) => s.category === "tide")
    ?.map(mapDataSourceToSelect)[0];
  const [swellSource, setSwellSource] = useState(initialSwellSource);
  const [tideSource, setTideSource] = useState(initialTideSource);
  const [swells, setSwells] = useState([]);
  const [tides, setTides] = useState([]);
  const addToast = useStoreActions((actions) => actions.toasts.addToast);
  // const { values, errors, touched, setFieldValue } = useFormikContext();

  useEffect(() => {
    setSwells(values.swells);
    setTides(values.tides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeSwell = (swell) => {
    const swellsCopy = [...swells];
    if (swell.id) {
      // if a swell has an id, it exists in the db and must be destroyed
      if (!swell._destroy) {
        // we don't need to do this to swell that already have destroy flag
        const removedSwell = { ...swell, _destroy: true };
        swellsCopy[swells.indexOf(swell)] = removedSwell;
        setSwells(swellsCopy);
      }
    } else {
      // otherwise just remove from array
      setSwells((prev) => prev.filter((s) => s !== swell));
    }
  };
  const removeSwells = (swellList) => {
    let swellsCopy = [...swells];
    swellList.forEach((swell) => {
      if (swell.id) {
        // if a swell has an id, it exists in the db and must be destroyed
        if (!swell._destroy) {
          // we don't need to do this to swell that already have destroy flag
          const removedSwell = { ...swell, _destroy: true };
          swellsCopy[swellsCopy.indexOf(swell)] = removedSwell;
        }
      } else {
        // otherwise just remove from array
        swellsCopy = swellsCopy.filter((s) => s !== swell);
      }
    });
    setSwells(swellsCopy);
  };
  const removeTides = (tideList) => {
    let tidesCopy = [...tides];
    tideList.forEach((tide) => {
      if (tide.id) {
        // if a swell has an id, it exists in the db and must be destroyed
        if (!tide._destroy) {
          // we don't need to do this to swell that already have destroy flag
          const removedTide = { ...tide, _destroy: true };
          tidesCopy[tidesCopy.indexOf(tide)] = removedTide;
        }
      } else {
        // otherwise just remove from array
        tidesCopy = tidesCopy.filter((t) => t !== tide);
      }
    });
    setTides(tidesCopy);
  };

  const swellFetchClick = () => {
    if (swells) {
      removeSwells(swells);
    }
    fetchSurflineSwells(swellSource?.uniqueId, formData.startTime);
  };
  const tideFetchClick = () => {
    if (tides) {
      removeTides(tides);
    }
    fetchSurflineTides(
      tideSource?.uniqueId,
      formData.startTime,
      formData.endTime
    );
  };

  const fetchSurflineSwells = async (spotId, startTime) => {
    SurflineAPI.getSwells(spotId).then(
      (res) => {
        const fetchedSwells = getSwellsforStartTime(res, startTime)?.slice(
          0,
          3
        );
        setSwells((prev) => {
          if (prev) {
            return [...prev, ...fetchedSwells];
          } else return fetchedSwells;
        });
      },
      (err) => {
        addToast({
          color: "danger",
          message: "Error fetching Swells. Surfline may be down.",
        });
        console.error(err);
      }
    );
  };
  const fetchSurflineTides = async (spotId, startTime, endTime) => {
    try {
      const result = await SurflineAPI.getTides(spotId);
      const fetchedTides = getTidesForSesh(result, startTime, endTime);
      setTides((prev) => {
        if (prev) {
          return [...prev, ...fetchedTides];
        } else return fetchedTides;
      });
    } catch (err) {
      addToast({
        color: "danger",
        message: "Error fetching Tides. Surfline may be down.",
      });
      console.error(err);
    }
  };

  methods.register("swellsAttributes", { required: true });
  methods.register("tidesAttributes", { required: true });
  useEffect(() => {
    methods.setValue("swellsAttributes", swells, { shouldDirty: true });
  }, [swells, methods]);
  useEffect(() => {
    methods.setValue("tidesAttributes", tides, { shouldDirty: true });
  }, [tides, methods]);

  useEffect(() => {
    if(!swells) {
      swellFetchClick();
    }
    if(!tides) {
      tideFetchClick();
    }
  }, [])
  return (
    <>
      <Row noGutters className="mt-1">
        <Col xs="auto">
          <h2>Tides and Swells</h2>
        </Col>
      </Row>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(next)}>
          <Row noGutters style={styles.section}>
            <Col xs="auto">
              <h3>Swells</h3>
            </Col>
          </Row>
          <Row>
            <Col>
              <Select
                options={spot?.dataSources
                  ?.filter((s) => s.category === "swell")
                  ?.map(mapDataSourceToSelect)}
                defaultValue={initialSwellSource}
                onChange={(source) => setSwellSource(source)}
              />
            </Col>
            <Col xs="auto">
              <Button color="primary" size="md" onClick={swellFetchClick}>
              Fetch swells
              </Button>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={12}>
              <Table responsive borderless style={styles.table}>
                <thead className="bg-light" style={styles.tableBorder}>
                  <tr>
                    <th>Height (ft)</th>
                    <th>Direction</th>
                    <th>Period (s)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="bg-light" style={styles.tableBorder}>
                  {swells
                    ?.filter((swell) => !swell._destroy)
                    ?.map((swell, ndx) => (
                      <tr key={ndx}>
                        <td>
                          <Input readOnly value={swell.height} />
                        </td>
                        <td>
                          <Input readOnly value={swell.direction} />
                        </td>
                        <td>
                          <Input readOnly value={swell.period} />
                        </td>
                        <td className="text-right">
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => removeSwell(swell)}
                          >
                            <FontAwesomeIcon icon={faTrash} size="xs" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  {swells?.length === 0 && (
                    <tr>
                      <td className="text-center" colSpan="3">
                        No Swells Added
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
          <Row noGutters style={styles.section}>
            <Col xs="auto">
              <h3>Tides</h3>
            </Col>
          </Row>
          <Row>
            <Col>
              <Select
                options={spot?.dataSources
                  ?.filter((s) => s.category === "tide")
                  ?.map(mapDataSourceToSelect)}
                defaultValue={initialTideSource}
                onChange={(source) => setTideSource(source)}
              />
            </Col>
            <Col xs="auto">
              <Button color="primary" size="md" onClick={tideFetchClick}>
                Fetch Tides
              </Button>
            </Col>
          </Row>
          <Row form className="mt-2">
            <Col xs={5}>
              <Label>Start Tide</Label>
              <Input
                step={0.1}
                readOnly
                value={
                  tides?.length
                    ? tides.filter((tide) => !tide._destroy)[0]?.height
                    : ""
                }
                type="number"
              />
            </Col>
            <Col xs={1} style={{ paddingLeft: 0 }}>
              <Button
                color="primary"
                outline
                disabled
                style={{ marginTop: 32 }}
              >
                {tides?.length > 0 &&
                tides.filter((tide) => !tide._destroy)[0]?.rising ? (
                  <FontAwesomeIcon icon={faLongArrowAltUp} />
                ) : (
                  <FontAwesomeIcon icon={faLongArrowAltDown} />
                )}
              </Button>
            </Col>
            <Col xs={5}>
              <Label>End Tide</Label>
              <Input
                step={0.1}
                readOnly
                value={
                  tides?.length
                    ? tides.filter((tide) => !tide._destroy)[1]?.height
                    : ""
                }
                type="number"
              />
            </Col>
            <Col xs={1} style={{ paddingLeft: 0 }}>
              <Button
                color="primary"
                outline
                disabled
                style={{ marginTop: 32 }}
              >
                {tides?.length > 1 &&
                tides.filter((tide) => !tide._destroy)[1]?.rising ? (
                  <FontAwesomeIcon icon={faLongArrowAltUp} />
                ) : (
                  <FontAwesomeIcon icon={faLongArrowAltDown} />
                )}
              </Button>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs="auto">
              <Button onClick={prev}>Previous</Button>
            </Col>
            <Col xs="auto" className="ml-auto">
              <Button
                color="primary"
                type="submit"
                disabled={!(swells?.length && tides?.length)}
              >
                Next
              </Button>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </>
  );
};

export default SwellFetcher;
